import { useState } from "react";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "../store";
import { useCrud } from "../useCrud";
import {
  Btn, Card, ConfirmDelete, Empty, Field, IconBtn, Modal, PageHead, Pill, Select, Table, Td, inputCls,
} from "../ui";

const blank = () => ({ name: "", role: "Employee", department: "", designation: "", status: "Active" });
const validate = (f) => {
  const e = {};
  if (!f.name?.trim()) e.name = "Name is required";
  if (!f.department) e.department = "Select a department";
  if (!f.designation) e.designation = "Select a designation";
  return e;
};

const PersonForm = ({ c, departments, designations }) => {
  const withCur = (list, v) => [...new Set([...list, v].filter(Boolean))];
  return (
    <form onSubmit={c.save} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="person-form">
      <Field label="Name *" error={c.errors.name} className="sm:col-span-2"><input className={inputCls} value={c.form.name} onChange={c.change("name")} data-testid="person-name-input" /></Field>
      <Field label="Role"><Select options={["Manager", "Employee"]} value={c.form.role} onChange={c.change("role")} data-testid="person-role-select" /></Field>
      <Field label="Status"><Select options={["Active", "Inactive"]} value={c.form.status} onChange={c.change("status")} data-testid="person-status-select" /></Field>
      <Field label="Department *" error={c.errors.department}><Select options={withCur(departments, c.form.department)} placeholder={departments.length ? "Select department" : "Add a department below first"} value={c.form.department} onChange={c.change("department")} data-testid="person-department-select" /></Field>
      <Field label="Designation *" error={c.errors.designation}><Select options={withCur(designations, c.form.designation)} placeholder={designations.length ? "Select designation" : "Add a designation below first"} value={c.form.designation} onChange={c.change("designation")} data-testid="person-designation-select" /></Field>
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Btn type="button" variant="ghost" onClick={c.close} data-testid="person-cancel-btn">Cancel</Btn>
        <Btn type="submit" data-testid="person-save-btn">Save person</Btn>
      </div>
    </form>
  );
};

const PeopleSection = () => {
  const { people, departments, designations, update } = useStore();
  const c = useCrud("people", blank, validate, "Person");
  const toggle = (p) => { const s = p.status === "Active" ? "Inactive" : "Active"; update("people", p.id, { status: s }); toast.success(`${p.name} is now ${s}`); };
  return (
    <Card className="animate-rise [animation-delay:60ms]">
      <div className="flex items-center justify-between border-b border-white/[0.07] px-5 py-4">
        <h2 className="font-heading text-base font-semibold text-zinc-100 md:text-lg">Employees / Managers</h2>
        <Btn onClick={() => c.openNew()} data-testid="add-person-btn"><Plus className="h-4 w-4" />Add Person</Btn>
      </div>
      {people.length ? (
        <Table head={["Name", "Role", "Department", "Designation", "Status", ""]}>
          {people.map((p) => (
            <tr key={p.id} className={`transition-colors hover:bg-white/[0.02] ${p.status === "Inactive" ? "opacity-60" : ""}`} data-testid={`person-row-${p.id}`}>
              <Td className="font-medium text-zinc-100">{p.name}</Td>
              <Td><Pill tone={p.role === "Manager" ? "blue" : "zinc"}>{p.role}</Pill></Td>
              <Td>{p.department}</Td>
              <Td>{p.designation}</Td>
              <Td>
                <div className="flex items-center">
                <button onClick={() => toggle(p)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${p.status === "Active" ? "bg-[#c6f432]" : "bg-zinc-700"}`} data-testid={`person-status-toggle-${p.id}`} aria-label="Toggle status">
                  <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-[#0b0d10] transition-transform ${p.status === "Active" ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
                <span className="ml-2 text-xs text-zinc-400">{p.status}</span>
                </div>
              </Td>
              <Td className="whitespace-nowrap">
                <IconBtn onClick={() => c.openEdit(p)} data-testid={`person-edit-${p.id}`}><Pencil className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={() => c.setDelId(p.id)} className="hover:text-rose-400" data-testid={`person-delete-${p.id}`}><Trash2 className="h-4 w-4" /></IconBtn>
              </Td>
            </tr>
          ))}
        </Table>
      ) : <Empty testid="people-empty" title="No people yet" hint="Add departments and designations below, then add your team here." />}
      <Modal open={!!c.form} onClose={c.close} title={c.editId ? "Edit Person" : "Add Person"} testid="person-modal">
        {c.form && <PersonForm c={c} departments={departments} designations={designations} />}
      </Modal>
      <ConfirmDelete open={!!c.delId} onCancel={() => c.setDelId(null)} onConfirm={c.confirmDelete} what="this person" />
    </Card>
  );
};

const ListItem = ({ value, onSave, onDelete, testid }) => {
  const [edit, setEdit] = useState(null);
  if (edit !== null)
    return (
      <li className="flex items-center gap-2 px-4 py-2">
        <input autoFocus className={inputCls} value={edit} onChange={(e) => setEdit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSave(edit) && setEdit(null)} data-testid={`${testid}-edit-input`} />
        <IconBtn onClick={() => onSave(edit) && setEdit(null)} data-testid={`${testid}-edit-save`}><Check className="h-4 w-4 text-emerald-400" /></IconBtn>
        <IconBtn onClick={() => setEdit(null)} data-testid={`${testid}-edit-cancel`}><X className="h-4 w-4" /></IconBtn>
      </li>
    );
  return (
    <li className="group flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-white/[0.02]" data-testid={testid}>
      <span className="text-sm text-zinc-200">{value}</span>
      <span className="flex">
        <IconBtn onClick={() => setEdit(value)} data-testid={`${testid}-edit`}><Pencil className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={onDelete} className="hover:text-rose-400" data-testid={`${testid}-delete`}><Trash2 className="h-4 w-4" /></IconBtn>
      </span>
    </li>
  );
};

const ListSection = ({ title, storeKey, personField, slug, delay }) => {
  const { [storeKey]: list, set } = useStore();
  const [val, setVal] = useState("");
  const [del, setDel] = useState(null);
  const clean = (v, except) => {
    const t = v.trim();
    if (!t) return toast.error(`${title.slice(0, -1)} name is required`), null;
    if (list.some((x) => x.toLowerCase() === t.toLowerCase() && x !== except)) return toast.error(`“${t}” already exists`), null;
    return t;
  };
  const add = (e) => { e.preventDefault(); const t = clean(val); if (!t) return; set(storeKey, (l) => [...l, t]); setVal(""); toast.success(`${t} added`); };
  const rename = (old) => (v) => {
    const t = clean(v, old);
    if (!t) return false;
    set(storeKey, (l) => l.map((x) => (x === old ? t : x)));
    set("people", (l) => l.map((p) => (p[personField] === old ? { ...p, [personField]: t } : p)));
    if (storeKey === "departments") set("meetings", (l) => l.map((m) => (m.department === old ? { ...m, department: t } : m)));
    toast.success(`Renamed to ${t}`);
    return true;
  };
  const remove = () => { set(storeKey, (l) => l.filter((x) => x !== del)); toast.success(`${del} deleted`); setDel(null); };
  return (
    <Card className="animate-rise" style={{ animationDelay: delay }}>
      <div className="border-b border-white/[0.07] px-5 py-4"><h2 className="font-heading text-base font-semibold text-zinc-100 md:text-lg">{title}</h2></div>
      <form onSubmit={add} className="flex gap-2 px-4 py-3">
        <input className={inputCls} value={val} onChange={(e) => setVal(e.target.value)} placeholder={`New ${title.slice(0, -1).toLowerCase()}`} data-testid={`${slug}-new-input`} />
        <Btn type="submit" data-testid={`${slug}-add-btn`}><Plus className="h-4 w-4" />Add</Btn>
      </form>
      {list.length ? (
        <ul className="divide-y divide-white/[0.05] border-t border-white/[0.05]">
          {list.map((x, i) => <ListItem key={x} value={x} onSave={rename(x)} onDelete={() => setDel(x)} testid={`${slug}-item-${i}`} />)}
        </ul>
      ) : <Empty testid={`${slug}-empty`} title={`No ${title.toLowerCase()} yet`} />}
      <ConfirmDelete open={!!del} onCancel={() => setDel(null)} onConfirm={remove} what={`“${del}”`} />
    </Card>
  );
};

const HoursSection = () => {
  const { settings, set } = useStore();
  const [h, setH] = useState(String(settings.problemHours));
  const save = (e) => {
    e.preventDefault();
    const n = Number(h);
    if (!(n > 0)) return toast.error("Hours must be greater than 0");
    set("settings", (s) => ({ ...s, problemHours: n }));
    toast.success(`Problem resolution time set to ${n} hours`);
  };
  return (
    <Card className="animate-rise p-5 [animation-delay:240ms]">
      <form onSubmit={save} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <Field label="Problem resolution hours" className="sm:w-64"><input type="number" min="1" className={inputCls} value={h} onChange={(e) => setH(e.target.value)} data-testid="problem-hours-input" /></Field>
        <Btn type="submit" data-testid="problem-hours-save-btn">Save</Btn>
        <p className="text-sm text-zinc-500 sm:ml-2 sm:pb-2">Open problems older than this are marked OVERDUE.</p>
      </form>
    </Card>
  );
};

export default function Settings() {
  return (
    <div className="space-y-6">
      <PageHead title="Settings" sub="Your team, departments, designations and rules." />
      <PeopleSection />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ListSection title="Departments" storeKey="departments" personField="department" slug="department" delay="120ms" />
        <ListSection title="Designations" storeKey="designations" personField="designation" slug="designation" delay="180ms" />
      </div>
      <HoursSection />
    </div>
  );
}
